import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of, Subscription} from 'rxjs';
import {StorageMap} from '@ngx-pwa/local-storage';
import {catchError, first, map} from 'rxjs/operators';
import {Router} from '@angular/router';
import {FormBuilder, Validators} from '@angular/forms';
import {RequestService} from "@services/request.service";
import {Customer} from "@models/customer";
import {StorageHelperService} from "@services/storage-helper.service";

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private user: Customer = null;
  public currentUserSubject = new BehaviorSubject<Customer>(null);
  public currentUser = new Observable<Customer>();
  private authenticated = false;

  constructor(
    private request: RequestService,
    private storage: StorageMap,
    private storageHelperService: StorageHelperService,
    private router: Router,
    private formBuilder: FormBuilder,
  ) {
    this.currentUser = this.currentUserSubject.asObservable();
    this.retrieveUserFromStorage$();
  }

  retrieveUserFromStorage$(): Observable<Customer> {
    return this.storageHelperService.get('currentUser');
    // return this.storage.get('currentUser') as Observable<Customer>;
  }

  login(credentials) {
    return this.request.post('/v1/auth/login', credentials).pipe(map(response => {
      this.setUser(response.customer);
      return {
        user: response.customer,
      };
    }));
  }

  notAuthenticated(){
    return !this.authenticated;
  }

  public setUser(customer: Customer): Customer {
    this.authenticated = true;
    this.user = customer;
    this.currentUserSubject.next(customer);
    this.storageHelperService.setObject('currentUser', customer);
    this.storageHelperService.setObject('token', customer.token);
    return customer;
  }

  public updateUser(user: Customer): Customer {
    if (user) {
      Object.assign(this.user, user);
    } else {
      this.user = null;
    }
    this.currentUserSubject.next(user);
    this.storage.set('currentUser', user).subscribe();
    return user;
  }

  register(params) {
    return this.request.post('api/auth/register', params).pipe(map(res => {
      return this.setUser(res.user);
    }));
  }

  forgotPassword(email) {
    return this.request.post('/v1/auth/forgot-password', {email});
  }

  validatePasswordToken(token) {
    return this.request.post('/v1/auth/validate-password-token', {token}).pipe(map(res => {
      return res.user as Customer;
    }));
  }

  resetPassword(params: { password: string; token: string }) {
    return this.request.post('/v1/auth/reset-password', params);
  }

  validateAccessToken(key) {
    return this.request.post('/v1/validateActivateToken', {key}).pipe(map(res => {
      return res.user;
    }));
  }

  activateAccount(params) {
    return this.request.post('/api/activateAccount', params).pipe(map(res => {
      return this.setUser(res.user);
    }));
  }

  generateOneTimeToken() {
    return this.request.get('/v1/auth/generate-one-time-token').pipe(map(response => response.token.token));
  }

  isAuthenticated(redirect = true): Observable<boolean> {
    // Are we authenticated already?
    // Here we'll check our local saved variable first, if it's false and we've landed on an authenticated route
    // then we'll want re-authenticate
    if (this.authenticated) {
      return of(true);
    } else {
      return this.request.post('/v1/auth/login').pipe( // we don't set credentials as we'll auto authenticate with token alone
        map(response => {
          if (response.success) {
            this.setUser(response.customer);
            return true;
          } else {
            this.storageHelperService.remove('currentUser')
            this.storageHelperService.remove('token');
            if(redirect){
              this.router.navigate(['/']).then();
            }
            return false;
          }
        }),
        catchError(() => {
          if(redirect) {
            this.router.navigate(['/auth/login']).then();
          }
          return of(false);
        }));
    }
  }

  logout(redirect = true): Subscription {
    this.storageHelperService.remove('currentUser');
    this.storageHelperService.remove('token');
    this.currentUserSubject.next(null);
    this.storage.clear().subscribe(() => {
      this.authenticated = false;
      if(redirect){
        this.router.navigate(['/']).then();
      }
    });
    return this.request.get('/v1/auth/logout').subscribe();
  }

  getLoginForm() {
    return this.formBuilder.group({
      username: ['', Validators.compose([Validators.required])],
      password: ['', Validators.required],
      client_type: ['web'],
      remember: [false],
    });
  }


}
